import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type {
    HomeReviewsAndMoreBlock as HomeReviewsAndMoreBlockValue,
    ImageChooserBlock,
    ReviewWithImageBlock,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { StarRating } from "../StarRating";

import styles from "./index.module.scss";

const imageFileSrc = (
    image: Immutable<ImageChooserBlock> | null | undefined,
): string => image?.imageData ?? image?.url ?? "";

const ReviewInfo = (
    props: Immutable<{ review: ReviewWithImageBlock; odd: boolean }>,
): React.JSX.Element => {
    const review = props.review.review;
    return (
        <div
            className={concatClassNames([
                styles.reviewsMoreReviewInfo,
                props.odd ? styles.reviewsMoreReviewInfoOdd : "",
            ])}
        >
            <StarRating
                rating={review?.stars}
                inverted
                className={styles.reviewsMoreReviewStarRatingContainer}
                ratingClassName={styles.reviewsMoreReviewStarRating}
                srLabel
            />
            <h3 className="home-oct16-reviews-more-review__copy">
                &quot;{review?.title}&quot;
            </h3>
            <p className={styles.reviewsMoreReviewAuthor}>{review?.author}</p>
            <p className={styles.reviewsMoreReviewLocation}>
                {review?.city}, {review?.state}
            </p>
        </div>
    );
};

const ReviewImage = (
    props: Immutable<{ review: ReviewWithImageBlock }>,
): React.JSX.Element => {
    return (
        <div
            className="home-oct16-reviews-more-review__image"
            style={{
                backgroundImage: `url(${imageFileSrc(props.review.image)})`,
            }}
            aria-hidden="true"
        />
    );
};

const ReviewItem = (
    props: Immutable<{ review: ReviewWithImageBlock; index: number }>,
): React.JSX.Element => {
    const odd = props.index + 1 >= 3;
    return (
        <li
            className={styles.reviewsMoreReview}
            role="group"
            aria-label="review"
        >
            {odd ? (
                <>
                    <ReviewInfo review={props.review} odd={true} />
                    <ReviewImage review={props.review} />
                </>
            ) : (
                <>
                    <ReviewImage review={props.review} />
                    <ReviewInfo review={props.review} odd={false} />
                </>
            )}
        </li>
    );
};

export const HomeReviewsAndMoreBlock = (
    props: Immutable<{
        value: HomeReviewsAndMoreBlockValue;
    }>,
): React.JSX.Element => {
    const value = props.value;
    return (
        <section
            className={concatClassNames([
                styles.reviewsMore,
                getGlobalSpacingClasses(value.spacing),
            ])}
        >
            <div className="main-container">
                <h2 className="home-oct16-reviews-more__title">
                    {value.title}
                </h2>
                <p className="home-oct16-reviews-more__copy">{value.copy}</p>
                <a
                    className="button button--secondary home-oct16-reviews-more__button"
                    href={value.link?.url ?? ""}
                >
                    Read <span>All</span> Reviews
                </a>
                <ul className={styles.reviewsMoreListAfter}>
                    {(value.reviews ?? []).map((review, i) => (
                        <ReviewItem key={i} review={review} index={i} />
                    ))}
                </ul>
            </div>
        </section>
    );
};
